/**
 * Chart image export — one implementation for every download menu.
 *
 * Three screens used to carry their own copy of this, and all three shared a
 * bug: ECharts can only produce an SVG when the chart was *drawn* as SVG, and
 * ours are drawn on a canvas. Asking a canvas for `image/svg` is not an error -
 * the browser silently substitutes PNG - so "Download as SVG" handed people a
 * PNG with an .svg name, which opens blank. See ECharts `getDataURL`:
 *
 *     painter.getType() === 'svg' ? getSvgDataURL() : renderToCanvas().toDataURL(...)
 *
 * So SVG here renders the chart a second time through an off-screen instance
 * that uses the SVG renderer, and returns real vector output.
 */
import * as echarts from 'echarts/core';
import { SVGRenderer } from 'echarts/renderers';

// The app draws charts on a canvas and registers only CanvasRenderer, so the
// SVG one has to be pulled in here or the off-screen export instance below has
// no renderer to use.
echarts.use([SVGRenderer]);

export type ChartImageFormat = 'png' | 'jpeg' | 'svg';

/** Redraw scale for raster formats. ECharts re-renders at this size rather
 *  than enlarging the on-screen picture, so text stays sharp. */
const EXPORT_SCALE = 4;

/** Browsers refuse to allocate a canvas past roughly 268M pixels and hand back
 *  a blank one instead of throwing. Stay well under and step the scale down
 *  for very large charts rather than exporting an empty image. */
const MAX_EXPORT_PIXELS = 200_000_000;

const EXPORT_BACKGROUND = '#ffffff';

/**
 * Re-quote font stacks so the SVG renderer can't emit broken markup.
 *
 * ECharts writes fontFamily straight into a double-quoted `style=""` attribute
 * without escaping it, so a stack like `"Inter", "Roboto"` closes the attribute
 * early and the file fails to parse - it opens as an error page rather than a
 * chart. Single quotes mean the same thing in CSS and are safe inside the
 * attribute. Our own theme already uses them; this covers anything else that
 * reaches the option, and keeps a future edit from silently breaking exports.
 */
function requoteFontFamilies(node: any): any {
  if (Array.isArray(node)) return node.map(requoteFontFamilies);
  if (node && typeof node === 'object') {
    const out: Record<string, any> = {};
    for (const key of Object.keys(node)) {
      const value = node[key];
      out[key] = key === 'fontFamily' && typeof value === 'string'
        ? value.replace(/"/g, "'")
        : requoteFontFamilies(value);
    }
    return out;
  }
  return node;
}

/**
 * A filename from the chart's own title, so downloads are identifiable in a
 * Downloads folder rather than a row of `chart-1788893760245.png`.
 */
export function chartFileName(title: string | undefined | null, format: ChartImageFormat): string {
  const slug = (title || 'chart')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
    .slice(0, 80) || 'chart';
  const today = new Date().toISOString().slice(0, 10);
  return `${slug}_${today}.${format}`;
}

/** Largest whole scale factor this chart can be redrawn at without exceeding
 *  the browser's canvas ceiling. */
function safeScale(width: number, height: number): number {
  if (!width || !height) return EXPORT_SCALE;
  let scale = EXPORT_SCALE;
  while (scale > 1 && width * scale * height * scale > MAX_EXPORT_PIXELS) {
    scale -= 1;
  }
  return scale;
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    triggerDownload(url, filename);
  } finally {
    // Give the click a tick to start before releasing the object URL.
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

function triggerDownload(href: string, filename: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Render the chart through a throwaway SVG-renderer instance.
 *
 * `getOption()` hands back the fully resolved option, so the copy matches what
 * is on screen - including the formatter functions the renderer revived. The
 * instance is off-screen, disposed straight after, and never sees a resize.
 */
function renderSvg(chart: echarts.ECharts): string {
  const host = document.createElement('div');
  host.style.cssText = 'position:absolute;left:-10000px;top:0;pointer-events:none;';
  host.style.width = `${chart.getWidth()}px`;
  host.style.height = `${chart.getHeight()}px`;
  document.body.appendChild(host);

  let offscreen: echarts.ECharts | null = null;
  try {
    offscreen = echarts.init(host, 'klaris', {
      renderer: 'svg',
      width: chart.getWidth(),
      height: chart.getHeight(),
    });
    offscreen.setOption(
      {
        ...requoteFontFamilies(chart.getOption() as Record<string, any>),
        backgroundColor: EXPORT_BACKGROUND,
        // The copy exists for one frame; an entry animation would export a
        // half-drawn chart.
        animation: false,
      },
      true /* notMerge */,
    );
    return offscreen.renderToSVGString();
  } finally {
    offscreen?.dispose();
    host.remove();
  }
}

/**
 * Save a chart as an image. Throws if the chart is gone or the browser refuses
 * the export, so callers can tell the user rather than failing silently.
 */
export function downloadChartImage(
  chart: echarts.ECharts | null | undefined,
  format: ChartImageFormat,
  title?: string | null,
): void {
  if (!chart || chart.isDisposed()) {
    throw new Error('The chart is no longer available to download.');
  }
  const filename = chartFileName(title, format);

  if (format === 'svg') {
    const svg = renderSvg(chart);
    // A Blob, not a base64 data URL: btoa() throws on any character outside
    // Latin-1, and our labels carry en dashes and ellipses.
    saveBlob(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }), filename);
    return;
  }

  const dataUrl = chart.getDataURL({
    type: format,
    pixelRatio: safeScale(chart.getWidth(), chart.getHeight()),
    backgroundColor: EXPORT_BACKGROUND,
  });
  if (!dataUrl) {
    throw new Error('The browser could not produce an image for this chart.');
  }
  triggerDownload(dataUrl, filename);
}
